import {
    Box,
    Button,
    Divider,
    Grid,
    IconButton,
    TextField,
    Typography,
    CircularProgress,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { ErrorMessage, Field, Form, Formik } from "formik";
import * as Yup from "yup";
import { Clear } from "@mui/icons-material";
import Autocomplete from "@mui/material/Autocomplete";
import Checkbox from "@mui/material/Checkbox";

import { color } from "../../../StyleData/Config";
import { axiosPrivate } from "../../../axios/axios";
import { masterAPI } from "../dataConfig";
import NormalTable from "../../../Component/NormalTable";
import ErrorProps from "../../../Component/Errorprops";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { defaultInfo } from "../../../ConfigInfo/Info";

const WorkCenterModal = ({ mode, handleCloseClick, editData }) => {

  const siteInfoDetail = useSelector((state) => state.auth.loginInfo);

  const [personData, setPersonData] = useState([]);
  const [selectPerson, setSelectPerson] = useState("");
  const [labourCraftData, setLabourCraftData] = useState([]);
  const [labourCraftSearch, setLabourCraftSearch] = useState({ name: '', craft: '' });
  const [loading, setLoading] = useState(false); // fix: define loading state
  const [selectedLabourCraft, setSelectedLabourCraft] = useState([]); // Track selected rows
  const [businessData, setBusinessData] = useState([]); // Business data
  const [selectedBusiness, setSelectedBusiness] = useState([]); // Selected business IDs
  const [businessLoading, setBusinessLoading] = useState(false); // Business loading state

  const handleCancel = () => handleCloseClick();

  useEffect(() => {
    getPersonList();
    getLabourCraftList();
    getBusinessList();

    if (mode === "Edit") {
      if (Array.isArray(editData.businessList)) {
        setSelectedBusiness(editData.businessList.map(b => b.businessId || b.id));
      } else if (Array.isArray(editData.businessIdList)) {
        setSelectedBusiness(editData.businessIdList);
      }

      if (Array.isArray(editData.labourList)) {
        setSelectedLabourCraft(editData.labourList.map(l => l.labourId || l.id));
      } else if (Array.isArray(editData.labourIdList)) {
        setSelectedLabourCraft(editData.labourIdList);
      }
    }
  }, [mode, editData]);

  const getPersonList = async () => {
     const link = (siteInfoDetail.roleId === defaultInfo.OrgAdmin.id) ? masterAPI["person"].link : masterAPI["person"].getallForSystemAdmin;
        
    await axiosPrivate
      .get(link)
      .then((res) => {
        const persons = res.data.result.map(item => ({
          id: item.id,
          name: item.personName
        }));
        setPersonData(persons);
        if (mode === "Edit" && Array.isArray(editData.responsiblePersonList)) {
          const selectedPerson = editData.responsiblePersonList.length > 0 ? editData.responsiblePersonList[0].id : "";
          setSelectPerson(selectedPerson);
        }
      })
      .catch((e) => {
        setPersonData([]);
        setSelectPerson("");
        console.log(e, "getPersons");
      });
  };

  const getLabourCraftList = async () => {
    await axiosPrivate
      .get(masterAPI["labour"].link)
      .then((res) => {
        setLabourCraftData(res.data.result || []);
      })
      .catch((e) => {
        setLabourCraftData([]);
        console.log(e, 'getLabourCraft');
      });
  };

  const getBusinessList = async () => {
    setBusinessLoading(true);
    await axiosPrivate
      .get(masterAPI["business"].link)
      .then((res) => {
        setBusinessData(res.data.result || []);
        setBusinessLoading(false);
      })
      .catch((e) => {
        setBusinessData([]);
        setBusinessLoading(false);
        console.log(e, 'getBusinessList');
      });
  };

  const initialValue = {
    code: mode === "Edit" ? editData.code : "",
    workCenterName: mode === "Edit" ? (editData.workCenterName || editData.name || "") : "",
    description: mode === "Edit" ? editData.description : "",
  };

  const validationSchema = Yup.object().shape({
    code: Yup.string().required("Work Center Code is required"),
    workCenterName: Yup.string().required("Work Center Name is required"),
  });

  const onSubmit = async (value) => {
    if (!selectPerson) {
      toast.error("Responsible Person is required");
      return;
    }

    if (selectedLabourCraft.length === 0) {
      toast.error("At least one Labour must be selected");
      return;
    }

    setLoading(true);
    const createLink = masterAPI["workCenterLabour"].create;
    const updateWorkCenter = masterAPI["workCenter"].update;
    const payload = {
      code: value.code,
      workCenterName: value.workCenterName,
      description: value.description,
      responsiblePersonIdList: selectPerson ? [selectPerson] : [],
      businessIdList: selectedBusiness,
      labourIdList: selectedLabourCraft,
    };
    if (mode === "Create") {
      await axiosPrivate
        .post(`${createLink}`, payload)
        .then(() => {
          setLoading(false);
          handleCloseClick();
        })
        .catch((e) => {
          setLoading(false);
          // toast.error(e.response?.data?.failureMessage || "Error creating work center");
        });
    } else {
      await axiosPrivate
        .put(`${updateWorkCenter}`, { ...payload, id: editData.id })
        .then(() => {
          setLoading(false);
          handleCloseClick();
        })
        .catch((e) => {
          setLoading(false);
          if (e.response?.data?.FailureMessage) {
            // toast.error("Code already exists");
          }
        });
    }
  };

  const filteredLabourCraft = labourCraftData.filter((item) => {
    const nameMatch = item.labourName?.toLowerCase().includes(labourCraftSearch.name.toLowerCase());
    const shiftMatch = item.shiftName?.toLowerCase().includes(labourCraftSearch.craft.toLowerCase());
    return nameMatch && shiftMatch;
  });

  const labourCraftColumns = [
    {
      id: 'select',
      Header: (
        <Checkbox
          checked={filteredLabourCraft.length > 0 && filteredLabourCraft.every(item => selectedLabourCraft.includes(item.id))}
          indeterminate={filteredLabourCraft.some(item => selectedLabourCraft.includes(item.id)) && !filteredLabourCraft.every(item => selectedLabourCraft.includes(item.id))}
          onChange={e => {
            if (e.target.checked) {
              setSelectedLabourCraft(filteredLabourCraft.map(item => item.id));
            } else {
              setSelectedLabourCraft([]);
            }
          }}
        />
      ),
      accessor: 'select',
      disableSortBy: true,
      disableFilters: true,
      Cell: ({ row }) => (
        <Checkbox
          checked={selectedLabourCraft.includes(row.original.id)}
          onChange={() => {
            setSelectedLabourCraft(prev =>
              prev.includes(row.original.id)
                ? prev.filter(id => id !== row.original.id)
                : [...prev, row.original.id]
            );
          }}
        />
      ),
    },
    { Header: 'Labour Name', accessor: 'labourName' },
  ];

  const uniqueBusinessData = React.useMemo(() => {
    const seen = new Set();
    return businessData.filter(item => {
      if (seen.has(item.businessName)) return false;
      seen.add(item.businessName);
      return true;
    });
  }, [businessData]);

  const businessColumns = [
    {
      id: 'select',
      Header: (
        <Checkbox
          checked={uniqueBusinessData.length > 0 && uniqueBusinessData.every(item => selectedBusiness.includes(item.id))}
          indeterminate={uniqueBusinessData.some(item => selectedBusiness.includes(item.id)) && !uniqueBusinessData.every(item => selectedBusiness.includes(item.id))}
          onChange={e => {
            if (e.target.checked) {
              setSelectedBusiness(uniqueBusinessData.map(item => item.id));
            } else {
              setSelectedBusiness([]);
            }
          }}
        />
      ),
      accessor: 'select',
      disableSortBy: true,
      disableFilters: true,
      Cell: ({ row }) => (
        <Checkbox
          checked={selectedBusiness.includes(row.original.id)}
          onChange={() => {
            setSelectedBusiness(prev =>
              prev.includes(row.original.id)
                ? prev.filter(id => id !== row.original.id)
                : [...prev, row.original.id]
            );
          }}
        />
      ),
    },
    { Header: 'Business Name', accessor: 'businessName' },
    // { Header: 'Craft Name', accessor: 'craftName' },
  ];

  return (
    <Box sx={{ p: color.masterPageDialogPadding }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <Typography
          sx={{
            color: color.masterDialogTitleColor,
            fontSize: color.masterDialogFontSize,
          }}
        >
          {mode === "Edit"
            ? `${editData.code || ''}-${editData.name || editData.workCenterName || ''}`
            : "New Work Center"}
        </Typography>
        <IconButton onClick={handleCancel}>
          <Clear />
        </IconButton>
      </Box>
      <Divider sx={{ border: "1px solid rgb(0 0 0 / 17%)", mb: "15px" }} />
      <Formik
        initialValues={initialValue}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {() => (
          <Form>
            <Grid container spacing={'1rem'}>
              <Grid item xs={12} md={6}>
                <Typography
                  sx={{
                    fontWeight: color.labelFontWeight,
                    mb: color.labelmb,
                    fontSize: color.labelFont,
                    color: color.labelColor,
                  }}
                >
                  Work Center Code <span style={{ color: "red" }}>*</span>
                </Typography>
                <Field
                  as={TextField}
                  size={"small"}
                  type="text"
                  name="code"
                  placeholder="Enter Work Center Code"
                  fullWidth
                  sx={{
                    ".MuiInputBase-input": {
                      "&::placeholder": {
                        opacity: 1,
                        color: color.placeholderColor,
                        fontSize: "13px",
                      },
                    },
                  }}
                />
                <ErrorMessage name="code">
                  {(error) => <ErrorProps>{error}</ErrorProps>}
                </ErrorMessage>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography
                  sx={{
                    fontWeight: color.labelFontWeight,
                    mb: color.labelmb,
                    fontSize: color.labelFont,
                    color: color.labelColor,
                  }}
                >
                  Work Center Name <span style={{ color: "red" }}>*</span>
                </Typography>
                <Field
                  as={TextField}
                  size={"small"}
                  type="text"
                  name="workCenterName"
                  placeholder="Enter Work Center Name"
                  fullWidth
                  sx={{
                    ".MuiInputBase-input": {
                      "&::placeholder": {
                        opacity: 1,
                        color: color.placeholderColor,
                        fontSize: "13px",
                      },
                    },
                  }}
                />
                <ErrorMessage name="workCenterName">
                  {(error) => <ErrorProps>{error}</ErrorProps>}
                </ErrorMessage>
              </Grid>
              <Grid item xs={12}>
                <Typography
                  sx={{
                    fontWeight: color.labelFontWeight,
                    mb: color.labelmb,
                    fontSize: color.labelFont,
                    color: color.labelColor,
                  }}
                >
                  Description
                </Typography>
                <Field
                  as={TextField}
                  size={"small"}
                  type="text"
                  name="description"
                  placeholder="Enter Description"
                  fullWidth
                  multiline
                  rows={2}
                  sx={{
                    ".MuiInputBase-input": {
                      "&::placeholder": {
                        opacity: 1,
                        color: color.placeholderColor,
                        fontSize: "13px",
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography
                  sx={{
                    fontWeight: color.labelFontWeight,
                    mb: color.labelmb,
                    fontSize: color.labelFont,
                    color: color.labelColor,
                  }}
                >
                  Responsible Person <span style={{ color: "red" }}>*</span>
                </Typography>
                <Autocomplete
                  options={personData}
                  getOptionLabel={(option) => option.name || ""}
                  value={personData.find((p) => p.id === selectPerson) || null}
                  onChange={(_, newValue) => setSelectPerson(newValue ? newValue.id : "")}
                  renderInput={(params) => (
                    <TextField {...params}  variant="outlined" size="small" />
                  )}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <Typography
                  sx={{ fontWeight: color.labelFontWeight, mb: color.labelmb, fontSize: color.labelFont, color: color.labelColor }}
                >
                  Labour <span style={{ color: "red" }}>*</span>
                </Typography>
                <NormalTable
                  data={filteredLabourCraft}
                  columns={labourCraftColumns}
                  filt={true}
                />
              </Grid>
              {/* Business Table */}
              <Grid item xs={12}>
                <Typography
                  sx={{ fontWeight: color.labelFontWeight, mb: color.labelmb, fontSize: color.labelFont, color: color.labelColor }}
                >
                  Business
                </Typography>
                <NormalTable
                  data={uniqueBusinessData}
                  columns={businessColumns}
                  filt={true}
                  loading={businessLoading}
                />
              </Grid>
              <Grid
                item
                xs={12}
                sx={{ display: "flex", justifyContent: "flex-end", gap: "1rem" }}
              >
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: color.TableCancelButton,
                    color: color.CancelTextColor,
                    textTransform: "none",
                    "&:hover": {
                      backgroundColor: color.TableCancelButton,
                    },
                  }}
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  type="submit"
                  disabled={loading}
                  sx={{
                    textTransform: "none",
                  }}
                >
                  {loading ? <CircularProgress size={24} /> : mode === "Edit" ? "Update" : "Submit"}
                </Button>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default WorkCenterModal;

